// This is strictly for development & debugging only.
// Do not use `window.devtools` from code, only from the console. It's intentionally not exported.
// Ony use the `devtoolsApi` from code to set stuff for `devtools` from the outside. That's the only thing we export from here.

import { cloneDeep } from '@dynamic/ui';
import copyText from 'copy-to-clipboard';
import { snapshot } from 'valtio';

import { StorageKeys } from '#/service/constants';

import { consoleIgnores, overrideConsole } from './console';
import { serviceListPromise } from './serviceList';

interface DevtoolsApi {
  setContexts(contexts: Contexts): void;
  setConfig(config: any): void;
  setStoryPageDef: (storyPageDef: any) => void;
  setPluginInfo: (id: string, info: any) => void;
  setIntegrationInfo: (bundleId: string, info: Integration) => void;
  setLayout: (layout: any) => void;
  overrideConsole: () => void;
  setStore(name: string, store: any, actions: any): void;
  setWdyr(initWdyr: Function): void;
}

declare global {
  interface Window {
    // It's `any` because we don't care about types in the DevTools (browser does not support them anyway)
    devtools?: any;
  }
}

interface Contexts {
  menu?: any;
  page?: any;
  analytics?: any;
}

interface Integration {
  oldPluginId: string;
  config: any;
  type: string;
}

// current context of devtools
interface Current {
  storyPageDef?: any;
  contexts: Contexts;
  config?: any;
  plugins: Record<string, any>;
  loadedIntegrations: Record<string, Integration>;
  layout?: any;
  stores: Record<string, any>;
  storeActions: Record<string, any>;
}

// copy a string, or object as json to the clipboard.
const copy = (data: any) => {
  if (typeof data !== 'string') {
    data = JSON.stringify(data);
  }
  copyText(data);
};

// flatten nested object, return entries
const flat = (obj: any, keypath = ''): any => {
  if (typeof obj !== 'object') {
    return [[keypath, obj]];
  }

  return Object.entries(obj).reduce((all, [key, value]) => {
    const nextKeypath = [keypath, key].filter(Boolean).join('.');
    const nextLevel = flat(value, nextKeypath);

    return all.concat(nextLevel);
  }, []);
};

// searching in the translations
const search = (filter?: string) => {
  const pattern = filter && new RegExp(filter, 'i');
  const flatEntries = flat(
    current.stores['translation'].translations
  ) as string[][];

  return Object.fromEntries(
    flatEntries.filter(
      ([key, value]) => !pattern || pattern.test(key) || pattern.test(value)
    )
  );
};

// search & replace of a property value in an object tree
const recursiveSetProp = (obj: any, propName: string, propValue: any) => {
  for (const key in obj) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      recursiveSetProp(obj[key], propName, propValue);
    } else if (key === propName) {
      obj[propName] = propValue;
    }
  }
};

// generalised handler maker for boolean settings
const booleanSettingHandler =
  (key: StorageKeys, title: string, message: string = '') =>
  (enabled?: boolean) => {
    const storedEnabled = localStorage.getItem(key) === 'true';
    const state = (enabled: boolean) =>
      `${title} is ${enabled ? 'enabled' : 'disabled'}.`;

    if (enabled === undefined) {
      return console.log(
        state(storedEnabled),
        'To change it, provide an argument (true/false).'
      );
    }

    localStorage.setItem(key, String(enabled));

    console.log(state(enabled), 'Reload the app to see the change.', message);
  };

const current: Current = {
  contexts: {},
  plugins: {},
  loadedIntegrations: {},
  stores: {},
  storeActions: {},
};

// devtools object that the user can access from the console.
const devtools = {
  // return all the contexts
  context: () => current.contexts,
  // shortcut for getting the config
  config: () => current.config,
  // shortcut for getting one or all feature configs
  feature: (name?: string) =>
    name ? current.config?.features?.[name] : current.config?.features,
  // return one or all stores
  store: (name?: string) => (name ? current.stores[name] : current.stores),
  // return one or all store action objects
  storeActions: (name?: string) =>
    name ? current.storeActions[name] : current.storeActions,
  // copy dynui pagedef to clipboard for putting into storybook.
  // see: one-dynamic-ui/storybook/exports/README.md
  story: () => {
    copy(current.storyPageDef);

    return 'Page definition copied to the clipboard.';
  },
  // navigate to another service
  service: (servicePath: string) => {
    serviceListPromise.then((serviceList) => {
      const queryString = serviceList[servicePath];

      if (queryString) {
        window.location.href = window.location.origin + '/' + queryString;
      } else {
        console.error('Invalid servicePath.');
        console.log('Valid:\n' + Object.keys(serviceList).join('\n'));
      }
    });
  },
  // get player info and version
  player: () => {
    const playerStore = current.stores['player'];
    const name = playerStore?.playerName ?? 'unknown';
    const version = playerStore?.pluginApi?.playerVersion?.() ?? 'unknown';

    if (!(name && version)) {
      return 'Player not loaded yet.';
    }

    return `Player name: ${name} - Version: ${version}`;
  },
  // get plugin info - for old "plugins"
  plugin: (filter?: string) => {
    const pattern = filter && new RegExp(filter, 'i');

    return Object.fromEntries(
      Object.entries(current.plugins).filter(
        ([key]) => !pattern || pattern.test(key)
      )
    );
  },
  // get integration info - for all integrations, including old "plugins"
  integration: (filters: string = '') => {
    if (filters === 'help') {
      return [
        'Filter with any number of full/partial plugin/bundle ID, activeForFeatures type, or special keywords:',
        'loaded/failed, old/new, client/backend (ex: "loaded client ads"). No parameter to list all.',
      ].join(' ');
    }

    const filterWords = filters.toLowerCase().split(' ');
    const { config, loadedIntegrations } = current;
    const { integrations, integrationsVersionOverrides, pluginIntegrations } =
      config;
    const allBundleIds = Object.keys({
      ...integrations,
      ...integrationsVersionOverrides,
      ...pluginIntegrations,
      ...loadedIntegrations,
    });

    return Object.fromEntries(
      allBundleIds
        .filter((bundleId) => {
          const isClient =
            bundleId.includes('one.client') ||
            loadedIntegrations[bundleId]?.oldPluginId;
          const searchableText = [
            bundleId,
            ...(integrations[bundleId]?.activeForFeatures ?? []),
            loadedIntegrations[bundleId] ? 'loaded' : 'failed',
            loadedIntegrations[bundleId]?.oldPluginId,
            bundleId.includes('.') ? 'new' : 'old',
            isClient ? 'client' : 'backend',
          ]
            .join(' ')
            .toLowerCase();

          return filterWords.every((word) => searchableText.includes(word));
        })
        .map((bundleId) => [
          bundleId,
          {
            config:
              loadedIntegrations[bundleId]?.config ??
              integrations[bundleId] ??
              'N/A',
            version: integrationsVersionOverrides[bundleId] ?? 'latest',
            mapping: pluginIntegrations[bundleId] ?? 'N/A',
            loaded: loadedIntegrations[bundleId] ?? 'N/A',
          },
        ])
    );
  },
  // last rendered dynui layout
  layout: () => current.layout,
  translations: (filter?: string) => {
    if (!filter) {
      return {
        ...cloneDeep(current.stores['translation']),
        search,
      };
    }

    return search(filter);
  },
  settings: {
    ads: booleanSettingHandler(StorageKeys.DEVTOOLS_ADS, 'Ads feature'),
    wdyr: booleanSettingHandler(StorageKeys.DEVTOOLS_WDYR, 'WhyDidYouRender'),
    consoleOverride: booleanSettingHandler(
      StorageKeys.DEVTOOLS_CONSOLE_OVERRIDE,
      'Console override'
    ),
    autoSlide: booleanSettingHandler(
      StorageKeys.DEVTOOLS_AUTO_SLIDE,
      'Auto-sliding'
    ),
    unauthHome: booleanSettingHandler(
      StorageKeys.DEVTOOLS_UNAUTH_HOME,
      'Unauthenticated homepage'
    ),
    locale: (locale?: string) => {
      if (!locale) {
        return localStorage.getItem(StorageKeys.LOCALE);
      }

      localStorage.setItem(StorageKeys.LOCALE, locale);
      console.log(
        'Locale has been updated. You may need to reload current page to see them applied.  Remember that if the locale you set doesnt match one-core locales it will default to fallback.'
      );
    },
  },
  consoleIgnores: () => consoleIgnores,
  // recursively remove the valtio proxy wrappers from an object
  unproxy: (proxy: any) => snapshot(proxy),
};

// api that the application can use to set stuff in devtools.
// always clone incoming data before storing it to avoid modification from the console or affecting garbage collection.
export const devtoolsApi: DevtoolsApi = {
  setContexts(contexts: Contexts) {
    const typedKeys = Object.keys(contexts) as (keyof Contexts)[];
    typedKeys.forEach((key) => {
      current.contexts[key] = cloneDeep(contexts[key]);
    });
  },
  setConfig(config: any) {
    current.config = cloneDeep(config);

    if (localStorage.getItem(StorageKeys.DEVTOOLS_UNAUTH_HOME) === 'false') {
      const { unauthenticatedHomepage = {} } = config?.featureConfig ?? {};
      unauthenticatedHomepage.enabled = false;
    }
  },
  setStoryPageDef: (storyPageDef: any) => {
    current.storyPageDef = cloneDeep(storyPageDef);
  },
  setPluginInfo: (id: string, info: any) => {
    current.plugins[id] = cloneDeep(info);
  },
  setIntegrationInfo: (bundleId: string, info: Integration) => {
    current.loadedIntegrations[bundleId ?? `«OLD» ${info.oldPluginId}`] =
      cloneDeep(info);
  },
  setLayout: (layout: any) => {
    current.layout = cloneDeep(layout);

    if (localStorage.getItem(StorageKeys.DEVTOOLS_AUTO_SLIDE) === 'false') {
      recursiveSetProp(layout, 'autoslide', false);
    }
  },
  setStore(name: string, store: any, actions: any) {
    // intentionally not cloning to preserve the proxies and allow modification
    current.stores[name] = store;
    current.storeActions[name] = actions;
  },
  setWdyr(initWdyr: Function) {
    if (localStorage.getItem(StorageKeys.DEVTOOLS_WDYR) === 'true') {
      initWdyr();
    }
  },
  overrideConsole,
};

/**
 * Let's use devtools code only in the development mode.
 * Webpack should nicely get rid of unused `devtools` code, so no one will be able to investigate source code of this piece
 */
if (process.env.goal === 'local') {
  window.devtools = devtools;
  console.debug('Devtools available');
}
